import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

/**
 * ChatInput Component
 * Input field with suggestions for the chat interface
 */
const ChatInput = ({
  onSend,
  disabled = false,
  isLoading = false,
  placeholder = 'Écrivez votre message...',
  suggestions = [],
  variant = 'default', // 'default' | 'search' | 'assistant'
}) => {
  const [message, setMessage] = useState('');
  const inputRef = useRef(null);

  // Focus input when component mounts
  useEffect(() => {
    if (inputRef.current && !disabled) {
      inputRef.current.focus();
    }
  }, [disabled]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (message.trim() && !disabled && !isLoading) {
      onSend(message.trim());
      setMessage('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setMessage(suggestion);
    inputRef.current?.focus();
  };

  return (
    <div className="space-y-3">
      {/* Suggestions */}
      {suggestions.length > 0 && !message && (
        <div className="flex flex-wrap gap-2">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              disabled={disabled || isLoading}
              className={cn(
                'text-xs px-3 py-1.5 rounded-full transition-all',
                'border border-gray-200 hover:border-orange-300',
                'bg-white hover:bg-orange-50 text-gray-600 hover:text-orange-700',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'shadow-sm hover:shadow'
              )}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}

      {/* Input form */}
      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isLoading}
            className={cn(
              'w-full px-4 py-3 pr-12',
              'bg-white border border-gray-200 rounded-xl',
              'text-sm text-gray-900 placeholder-gray-400',
              'focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400',
              'disabled:opacity-50 disabled:bg-gray-50',
              'transition-all duration-200'
            )}
          />
        </div>

        <Button
          type="submit"
          disabled={!message.trim() || disabled || isLoading}
          size="icon"
          className={cn(
            'h-12 w-12 rounded-xl shrink-0',
            'bg-gradient-to-r from-[#FF9F68] to-[#E67E4F] hover:from-[#E67E4F] hover:to-[#FF9F68]',
            'shadow-md hover:shadow-lg transition-all duration-200',
            'disabled:opacity-50 disabled:shadow-none'
          )}
        >
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Send className="h-5 w-5" />
          )}
        </Button>
      </form>
    </div>
  );
};

export default ChatInput;

